/**
 * useOnboardingChecklist
 *
 * Powers the getting-started checklist. Each task auto-completes from real data
 * (does the user have a recipe? a planned meal? a shopping list? a shared group?)
 * and a manual override (tick/untick) takes precedence when set.
 *
 * Lightweight queries only run while the checklist is relevant (authenticated and
 * not dismissed), so finished users incur no ongoing cost.
 */

import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { storeToRefs } from 'pinia'
import { GET_RECIPES, GET_MEALS, GET_SHOPPING_LISTS } from '../graphql/queries'
import { useAuthStore } from '../stores/auth'
import { useOnboardingStore } from '../stores/onboarding'
import { CHECKLIST_TASKS } from '../config/onboardingTour'

export interface ChecklistItem {
  id: string
  label: string
  description: string
  details: string
  cta: string
  tourStepId: string
  route: string
  /** Effective state shown to the user (override ?? auto-detected). */
  done: boolean
  /** What the data says, ignoring any manual override. */
  autoDone: boolean
  /** Whether the user has manually overridden this task. */
  overridden: boolean
}

export function useOnboardingChecklist() {
  const authStore = useAuthStore()
  const onboarding = useOnboardingStore()
  const { taskOverrides, checklistDismissed } = storeToRefs(onboarding)

  const isAuthenticated = computed(() => authStore.isAuthenticated)
  // Only fetch while the checklist could actually be shown.
  const enabled = computed(() => isAuthenticated.value && !checklistDismissed.value)

  const { result: recipesResult } = useQuery(GET_RECIPES, { limit: 1, page: 1 }, () => ({ enabled: enabled.value }))
  const { result: mealsResult } = useQuery(GET_MEALS, null, () => ({ enabled: enabled.value }))
  const { result: listsResult } = useQuery(GET_SHOPPING_LISTS, null, () => ({ enabled: enabled.value }))

  // Auto-detection per task.
  const autoDone = computed<Record<string, boolean>>(() => ({
    recipe: (recipesResult.value?.recipes?.length ?? 0) > 0,
    meal: (mealsResult.value?.meals?.length ?? 0) > 0,
    shopping: (listsResult.value?.shoppingLists?.length ?? 0) > 0,
    // A non-personal (shared) group means the user has started collaborating.
    // authStore.groups is already loaded at login — no extra query needed.
    group: (authStore.groups ?? []).some((g) => !(g as { is_personal?: boolean }).is_personal),
  }))

  const items = computed<ChecklistItem[]>(() =>
    CHECKLIST_TASKS.map((t) => {
      const override = taskOverrides.value[t.id]
      const auto = autoDone.value[t.id] ?? false
      return {
        ...t,
        autoDone: auto,
        overridden: override !== undefined,
        done: override !== undefined ? override : auto,
      }
    }),
  )

  const completedCount = computed(() => items.value.filter((i) => i.done).length)
  const total = computed(() => items.value.length)
  const allComplete = computed(() => total.value > 0 && completedCount.value === total.value)
  const progress = computed(() => (total.value ? Math.round((completedCount.value / total.value) * 100) : 0))

  // True once the auto-detection queries have returned, so the widget can avoid
  // flashing a "0 of 4" state before data loads.
  const settled = computed(
    () =>
      !enabled.value ||
      (recipesResult.value !== undefined && mealsResult.value !== undefined && listsResult.value !== undefined),
  )

  return { items, completedCount, total, allComplete, progress, settled }
}
